import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, ClipboardList, FolderKanban, Users, Truck,
  FileSignature, Settings, LogOut, HardHat, Menu, X, Coins, BarChart3,
} from "lucide-react";

const nav = [
  { to: "/", label: "Översikt", icon: LayoutDashboard, roles: ["owner", "partner", "employee"], end: true },
  { to: "/worklog", label: "Tidrapport", icon: ClipboardList, roles: ["owner", "partner", "employee"] },
  { to: "/overview", label: "Projektöversikt", icon: BarChart3, roles: ["owner", "partner"] },
  { to: "/projects", label: "Projekt", icon: FolderKanban, roles: ["owner", "partner"] },
  { to: "/team", label: "Personal", icon: Users, roles: ["owner", "partner"] },
  { to: "/resources", label: "Maskiner & Material", icon: Truck, roles: ["owner", "partner"] },
  { to: "/prices", label: "Priser", icon: Coins, roles: ["owner", "partner"] },
  { to: "/signoffs", label: "Attest & Faktura", icon: FileSignature, roles: ["owner", "partner"] },
  { to: "/settings", label: "Företag", icon: Settings, roles: ["owner", "partner"] },
];

export const roleLabel = (role) =>
  role === "owner" ? "Ägare" : role === "partner" ? "Delägare" : "Anställd";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav2 = useNavigate();
  const [open, setOpen] = useState(false);
  const items = nav.filter((n) => n.roles.includes(user?.role));

  const SidebarInner = () => (
    <>
      <div className="flex items-center gap-2.5 px-5 py-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500 text-slate-950">
          <HardHat size={20} />
        </div>
        <div className="leading-tight">
          <div className="font-display text-sm font-bold text-white">TJ Entreprenad</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Fältrapportering</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1 px-3 py-2">
        {items.map((n) => (
          <NavLink
            key={n.to} to={n.to} end={n.end}
            data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                isActive
                  ? "bg-amber-500/10 text-amber-400 border-l-2 border-amber-500"
                  : "text-slate-400 hover:bg-slate-800/60 hover:text-white border-l-2 border-transparent"
              }`
            }
          >
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-800 p-3">
        <div className="mb-2 px-2">
          <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
          <div className="text-xs text-slate-500 capitalize">{roleLabel(user?.role)}</div>
        </div>
        <button data-testid="logout-button" onClick={() => { logout(); nav2("/login"); }}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-colors">
          <LogOut size={18} /> Logga ut
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-slate-950">
      <aside className="hidden w-64 flex-col border-r border-slate-800 bg-slate-900/40 lg:flex fixed inset-y-0">
        <SidebarInner />
      </aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-950/70" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-64 flex-col border-r border-slate-800 bg-slate-900">
            <button onClick={() => setOpen(false)} className="absolute right-3 top-4 text-slate-400"><X size={20} /></button>
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-slate-800 bg-slate-950/80 px-4 py-3 backdrop-blur lg:hidden">
          <button data-testid="mobile-menu-button" onClick={() => setOpen(true)} className="text-slate-300"><Menu size={22} /></button>
          <span className="font-display font-bold text-white">TJ Entreprenad</span>
        </header>
        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-10 lg:py-8">{children}</main>
      </div>
    </div>
  );
}
